// File: frontend/src/hooks/useDocumentMeta.ts
import { useEffect } from "react";

/**
 * Values this hook writes into <head>. All optional so a page can wait
 * until its data has loaded before calling this hook with real values —
 * see usage in PersonPage / ClubPage / TeamPage / CustomTopicPage.
 */
export interface DocumentMetaInput {
  /** Full page title, e.g. "Galatasaray - Üç Korner Bir Penaltı". */
  title?: string;
  /** Meta description shown in search result snippets (~150-160 chars is the practical limit). */
  description?: string;
  /** Absolute canonical URL for this page, e.g. "https://uckornerbirpenalti.com/kulup/galatasaray". */
  canonicalUrl?: string;
  /** og:image override — falls back to the site default (logo512.png) baked into index.html when omitted. */
  imageUrl?: string;
}

// The static defaults baked into frontend/public/index.html at build time.
// Restored on unmount so navigating away from an entity page (e.g. back to
// the home feed) doesn't leave a stale club/person title behind.
// Must stay identical to the <title>, og:site_name and WebSite JSON-LD "name"
// in frontend/public/index.html — Google derives the site name shown in search
// results from those signals agreeing with each other.
const DEFAULT_TITLE = "Üç Korner Bir Penaltı";
const DEFAULT_DESCRIPTION =
  "Üç Korner Bir Penaltı - Dijital kahvehaneniz. Gündem, maçlar, kulüpler, takımlar ve futbolcular hakkında tanımlar yazın, tartışın, oylayın.";

/**
 * Sets document.title and the description / canonical / Open Graph / Twitter
 * meta tags for the currently mounted page, then restores the site-wide
 * defaults on unmount.
 *
 * Deliberately implemented as a plain useEffect + direct DOM writes instead
 * of pulling in react-helmet(-async): this is a Create React App SPA with no
 * SSR, so there is no server-render timing issue helmet solves here — a
 * dozen lines of direct DOM manipulation covers the same need without a new
 * dependency. Each meta tag is looked up by its existing attribute (name= or
 * property=) rather than replaced wholesale, so tags not covered by this
 * hook (viewport, theme-color, google-site-verification, favicons, ...)
 * are left untouched.
 *
 * Values are optional and skipped (tag left as-is) when omitted — pages call
 * this with real values only once their entity data has finished loading,
 * so a slow API response never causes a flash of an empty/wrong title.
 */
export function useDocumentMeta(meta: DocumentMetaInput): void {
  useEffect(() => {
    if (!meta.title && !meta.description && !meta.canonicalUrl && !meta.imageUrl) {
      return;
    }

    const previousTitle = document.title;

    if (meta.title) {
      document.title = meta.title;
    }

    if (meta.description) {
      setMetaByName("description", meta.description);
      setMetaByProperty("og:description", meta.description);
      setMetaByName("twitter:description", meta.description);
    }

    if (meta.title) {
      setMetaByProperty("og:title", meta.title);
      setMetaByName("twitter:title", meta.title);
    }

    if (meta.canonicalUrl) {
      setCanonicalLink(meta.canonicalUrl);
      setMetaByProperty("og:url", meta.canonicalUrl);
    }

    if (meta.imageUrl) {
      setMetaByProperty("og:image", meta.imageUrl);
      setMetaByName("twitter:image", meta.imageUrl);
    }

    // Restore the site-wide defaults when this page unmounts (route change),
    // so the next page starts from a clean slate rather than inheriting
    // whichever entity was viewed last.
    return () => {
      document.title = previousTitle || DEFAULT_TITLE;
      if (meta.description) {
        setMetaByName("description", DEFAULT_DESCRIPTION);
        setMetaByProperty("og:description", DEFAULT_DESCRIPTION);
        setMetaByName("twitter:description", DEFAULT_DESCRIPTION);
      }
      if (meta.title) {
        setMetaByProperty("og:title", DEFAULT_TITLE);
        setMetaByName("twitter:title", DEFAULT_TITLE);
      }
      if (meta.canonicalUrl) {
        removeCanonicalLink();
        setMetaByProperty("og:url", "https://uckornerbirpenalti.com/");
      }
      if (meta.imageUrl) {
        const defaultImage = "https://uckornerbirpenalti.com/logo512.png";
        setMetaByProperty("og:image", defaultImage);
        setMetaByName("twitter:image", defaultImage);
      }
    };
    // Re-run whenever any input value changes (e.g. navigating from one
    // club page directly to another without unmounting the wrapper).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.title, meta.description, meta.canonicalUrl, meta.imageUrl]);
}

function setMetaByName(name: string, content: string): void {
  let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setMetaByProperty(property: string, content: string): void {
  let tag = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonicalLink(url: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

function removeCanonicalLink(): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (link) {
    link.parentNode?.removeChild(link);
  }
}